// CRAP is inflated only when the CI fallow coverage step times out (0% scoring, see
// .github/workflows/fallow.yml); these branchy fns are fully unit-tested. Drop when fixed.
// fallow-ignore-file complexity
// @plasser/ui — EntityChip: compact, optionally-removable representation of a
// selected entity (person, group, machine…). Used in pickers, assignment cells
// and filter bars.

import * as React from "react";
import { X } from "lucide-react";

import { cn } from "../lib/utils";

export interface EntityChipProps {
  label: string;
  /** Leading icon or avatar node. */
  icon?: React.ReactNode;
  onRemove?: () => void;
  onClick?: () => void;
  tone?: "default" | "muted" | "primary";
  /** Accessible label for the remove button (default German: "<label> entfernen"). */
  removeLabel?: (label: string) => string;
  className?: string;
  "data-testid"?: string;
}

const toneClass: Record<NonNullable<EntityChipProps["tone"]>, string> = {
  default: "border-border bg-background text-foreground",
  muted: "border-transparent bg-muted text-muted-foreground",
  primary: "border-primary/30 bg-primary/10 text-primary",
};

export function EntityChip({
  label,
  icon,
  onRemove,
  onClick,
  tone = "default",
  removeLabel = (l) => `${l} entfernen`,
  className,
  ...rest
}: EntityChipProps): React.ReactElement {
  const body = (
    <>
      {icon ? <span className="shrink-0">{icon}</span> : null}
      <span className="truncate">{label}</span>
    </>
  );

  return (
    // Presentational container only — NOT interactive. Keeping the outer element
    // role-less means the clickable label and the remove button stay SIBLINGS, so
    // we never nest one interactive control inside another (which would be invalid
    // ARIA and double-announce in screen readers — the clickable + removable chip
    // is exactly the AssignmentCell assigned state).
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
        toneClass[tone],
        className,
      )}
      data-testid={rest["data-testid"] ?? "entity-chip"}
    >
      {onClick ? (
        // A real <button> → native keyboard activation (Enter/Space) + focus.
        <button
          type="button"
          onClick={onClick}
          data-testid="entity-chip-button"
          className="inline-flex min-w-0 items-center gap-1 rounded-full outline-none hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring"
        >
          {body}
        </button>
      ) : (
        body
      )}
      {onRemove ? (
        <button
          type="button"
          aria-label={removeLabel(label)}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 shrink-0 rounded-full hover:text-destructive"
        >
          <X className="size-3" />
        </button>
      ) : null}
    </span>
  );
}
